// ============================================
// Longitudinal Biomarker History Service
//
// UC-2: Fetches prior biomarker results for a patient
// and formats them for the Captain's analysis prompt.
//
// Data source: lab_sessions collection (completed sessions
// with extractedBiomarkers)
// ============================================

import { getLabSessionsForPatient } from '../firestore/labSessionService';
import type { LongitudinalBiomarkerData, PriorBiomarkerValue } from '../../types';
import type { LabSession } from '../../types/labUpload';

/**
 * Fetch prior biomarker results for a patient from completed lab sessions.
 * Returns structured data keyed by biomarker name for easy lookup.
 *
 * @param patientId - The patient's UID
 * @param excludeSessionId - Current session ID to exclude from history
 * @param maxSessions - Maximum number of prior sessions to fetch (default: 5)
 */
export async function getPriorBiomarkerResults(
  patientId: string,
  excludeSessionId?: string,
  maxSessions = 5,
): Promise<LongitudinalBiomarkerData | null> {
  if (!patientId) return null;

  try {
    // Fetch recent completed sessions, +1 to account for possible exclusion
    const sessions = await getLabSessionsForPatient(patientId, maxSessions + 1);

    // Filter to completed sessions with extracted biomarkers, excluding current
    const priorSessions = sessions.filter(
      (s: LabSession) =>
        s.status === 'complete' &&
        s.extractedBiomarkers &&
        s.extractedBiomarkers.length > 0 &&
        s.sessionId !== excludeSessionId,
    ).slice(0, maxSessions);

    if (priorSessions.length === 0) return null;

    // Build lookup: biomarker name → array of prior values (most recent first)
    const priorResults: Record<string, PriorBiomarkerValue[]> = {};

    for (const session of priorSessions) {
      for (const marker of session.extractedBiomarkers) {
        if (marker.value === null || marker.value === undefined) continue;

        const name = marker.rawName || marker.canonicalId || '';
        if (!name) continue;

        if (!priorResults[name]) {
          priorResults[name] = [];
        }

        priorResults[name].push({
          value: marker.value,
          unit: marker.unit,
          date: session.uploadedAt,
        });
      }
    }

    if (Object.keys(priorResults).length === 0) return null;

    return {
      patientId,
      priorResults,
      priorSessionCount: priorSessions.length,
    };
  } catch (error) {
    console.warn('[Longitudinal] Failed to fetch prior results:', error);
    // Non-fatal: Captain can still analyze current results without history
    return null;
  }
}

/**
 * Format prior biomarker data into a text block for inclusion in the Captain's
 * user message prompt. Returns empty string if no prior data.
 */
export function formatPriorResultsForPrompt(
  longitudinalData: LongitudinalBiomarkerData | null,
): string {
  if (!longitudinalData || Object.keys(longitudinalData.priorResults).length === 0) {
    return '';
  }

  const lines: string[] = [];
  lines.push(`\n\n===== PRIOR LAB RESULTS (${longitudinalData.priorSessionCount} previous session${longitudinalData.priorSessionCount > 1 ? 's' : ''}) =====`);
  lines.push('Compare these prior values with the current results to identify trends.\n');

  for (const [biomarkerName, values] of Object.entries(longitudinalData.priorResults)) {
    const entries = values
      .map((v) => `${v.value} ${v.unit} (${formatDate(v.date)})`)
      .join(' → ');
    lines.push(`- ${biomarkerName}: ${entries}`);
  }

  lines.push('\nFor each biomarker where prior data exists, include a "trajectory" object in your output.');
  lines.push('The trajectory should describe the direction of change (improving/worsening/stable/new)');
  lines.push('and include a human-readable summary like "Your Vitamin D went from 22 to 48 ng/mL over 3 months".');

  return lines.join('\n');
}

/** Format ISO date to readable form: "Jan 15, 2026" */
function formatDate(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}
